// Apolo IDE — Plan phase estimates (sprint plan-phase-estimates).
//
// Função PURA: olha os blocks do canvas e estima tempo + complexidade por
// fase, com defaults de ROI/KPIs editáveis. Reusa as PHASES da heurística
// de sugestões. Sem React, sem efeitos — testável isolada.

import { PHASES } from './suggestions.js';

// Peso (horas) por tipo de bloco — usado pra estimar esforço por fase.
export const KIND_HOURS = {
  skill: 2,
  todo: 1.5,
  text: 1,
  table: 1,
  mermaid: 1,
  chart: 1,
  chat: 0.5,
  'chat-ref': 0.5,
  link: 0.5,
  file: 0.5,
  pdf: 0.5,
  image: 0.5,
  video: 0.5,
  embed: 0.5,
  heading: 0.25,
  sticky: 0.25,
  divider: 0,
};

// KPIs sugeridos por fase (starter — editável pelo usuário).
export const PHASE_KPI_DEFAULTS = {
  discovery:    ['Hipóteses validadas', 'Entrevistas realizadas'],
  architecture: ['ADRs documentados', 'Diagramas aprovados'],
  database:     ['Queries < 100ms', 'Migrations com rollback'],
  delivery:     ['Lead time de deploy', 'Frequência de deploy'],
  security:     ['0 vulnerabilidades críticas', 'Cobertura de secret scan'],
  qa:           ['Cobertura de testes %', 'Bugs escapados pra produção'],
};

const PHASE_BY_ID = Object.fromEntries(PHASES.map((p) => [p.id, p]));

function roundHalf(n) {
  return Math.round(n * 2) / 2;
}

function complexityOf(hours) {
  if (hours < 2) return 'Baixa';
  if (hours <= 6) return 'Média';
  return 'Alta';
}

/**
 * Estima tempo/complexidade/ROI/KPIs por fase a partir dos blocks.
 * Só inclui fases com pelo menos 1 bloco com aquele `_phase`.
 *
 * @param {Array} blocks
 * @param {Record<string,{hours?:number,roi?:string,kpis?:string[]}>} [overrides]
 *   overrides do usuário por phaseId (persistidos em localStorage).
 * @returns {{ phases: Array, totalHours: number }}
 */
export function computePhaseEstimates(blocks, overrides = {}) {
  const list = Array.isArray(blocks) ? blocks : [];
  const hoursByPhase = new Map(); // phaseId -> hours acumuladas

  for (const b of list) {
    if (!b) continue;
    const ph = b.payload?._phase;
    if (!ph || !PHASE_BY_ID[ph]) continue;
    const h = KIND_HOURS[b.kind] ?? 0.5;
    hoursByPhase.set(ph, (hoursByPhase.get(ph) || 0) + h);
  }

  // Ordem canônica das PHASES; só as presentes.
  const phases = [];
  let totalHours = 0;
  for (const p of PHASES) {
    if (!hoursByPhase.has(p.id)) continue;
    const ov = overrides[p.id] || {};
    const baseHours = Math.max(0.5, roundHalf(hoursByPhase.get(p.id)));
    const hours = Number.isFinite(ov.hours) && ov.hours >= 0 ? ov.hours : baseHours;
    const roi = typeof ov.roi === 'string' && ov.roi.trim()
      ? ov.roi.trim()
      : `Economia estimada: ~${hours}h/sprint`;
    const kpis = Array.isArray(ov.kpis) && ov.kpis.length
      ? ov.kpis.filter(Boolean)
      : (PHASE_KPI_DEFAULTS[p.id] || []);
    totalHours += hours;
    phases.push({
      id: p.id,
      label: p.label,
      emoji: p.emoji,
      hours,
      complexity: complexityOf(hours),
      roi,
      kpis,
    });
  }

  return { phases, totalHours: roundHalf(totalHours) };
}
